import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import {
  downloadFile,
  shareMessage,
  shareURL,
} from "@telegram-apps/sdk-react";
import { useFetch } from "@/lib/axios.config";

interface RenderOptions {
  node: HTMLElement;
  completedAt: Date;
  backgroundColor: string;
}

interface SaveOrShareOptions {
  blob: Blob;
  fileName: string;
  /**
   * Required for the mobile-Telegram path: when share isn't usable on
   * Android Telegram WebView, the helper falls back to a backend
   * endpoint that has the bot drop the PDF straight into the user's
   * Telegram chat (matching how Click / Payme deliver receipts).
   */
  transferId?: string | null;
}

function getTgPlatform(): string {
  return (
    (window as unknown as {
      Telegram?: { WebApp?: { platform?: string } };
    }).Telegram?.WebApp?.platform || "unknown"
  );
}

function isMobileTelegram(): boolean {
  const p = getTgPlatform();
  return p === "android" || p === "ios";
}

// Android Telegram WebView silently no-ops BOTH `shareMessage` and
// `shareURL`: the SDK reports them as available and the promises
// resolve successfully, but no native share UI ever appears. We branch
// on this in `shareReceipt` to bypass every share API on Android and
// drop the PDF straight into the user's chat via the backend bot —
// same UX as Click / Payme. html-to-image (the client renderer) also
// hangs on Android WebView, so the bot path uses the backend-rendered
// PDF instead.
function isAndroidTelegram(): boolean {
  return getTgPlatform() === "android";
}

// Any Telegram client — we gate the WebApp.shareMessage path on this
// so non-Telegram browsers don't try a Telegram-SDK call.
function isAnyTelegramWebApp(): boolean {
  const p = getTgPlatform();
  return (
    p === "android" ||
    p === "ios" ||
    p === "macos" ||
    p === "tdesktop" ||
    p === "weba" ||
    p === "webk"
  );
}

// Forward the PDF to the user's Telegram chat via the backend bot.
// Backend: POST /wallet/SendReceiptToChat (multipart). Returns true on
// success so the caller can swap "Chek Telegram chatga yuborildi" for
// the generic share confirmation.
async function sendReceiptToChat(
  blob: Blob,
  fileName: string,
  transferId: string
): Promise<boolean> {
  const form = new FormData();
  form.append("transfer_id", transferId);
  form.append("file", blob, fileName);
  try {
    await useFetch.post("wallet/SendReceiptToChat", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return true;
  } catch (err) {
    console.error("send receipt to chat failed:", err);
    return false;
  }
}


// Backend prepares a Telegram inline message attached to the PDF and
// returns its prepared_message_id (for shareMessage on Mini Apps v8+)
// plus a token-signed public share_url (for shareURL on older clients
// — Android Telegram WebView in particular doesn't always honour
// shareMessage). shareReceipt picks whichever the runtime supports.
interface PreparedShare {
  preparedMessageId: string | null;
  shareUrl: string | null;
}

async function fetchPreparedShare(
  transferId: string
): Promise<PreparedShare | null> {
  try {
    const res = await useFetch.post<{
      prepared_message_id: string | null;
      expiration_date?: number;
      share_url?: string;
    }>(
      "wallet/PrepareReceiptShare",
      { transfer_id: transferId },
      { timeout: 20_000 }
    );
    return {
      preparedMessageId: res.data?.prepared_message_id ?? null,
      shareUrl: res.data?.share_url ?? null,
    };
  } catch (err) {
    console.error("prepare receipt share failed:", err);
    return null;
  }
}

// Pull the rendered PDF as a Blob from the backend. Used by the
// native-share path: we hand the blob to navigator.share so the user
// gets the OS share sheet (Telegram contact picker, AirDrop, Save to
// Files, etc.) instead of having the PDF auto-dropped in the bot chat.
// Returns null on failure so the caller can fall back gracefully.
async function fetchReceiptPdfBlob(
  transferId: string
): Promise<{ blob: Blob; fileName: string } | null> {
  try {
    const res = await useFetch.post(
      "wallet/RenderReceiptPdf",
      { transfer_id: transferId },
      { timeout: 15_000, responseType: "blob" }
    );
    const blob = res.data as Blob;
    return { blob, fileName: `don-chek-${transferId}.pdf` };
  } catch (err) {
    console.error("fetch receipt pdf failed:", err);
    return null;
  }
}

function buildPublicReceiptPdfUrl(
  receiptPageUrl: string,
  transferId: string
): string | null {
  try {
    const pageUrl = new URL(receiptPageUrl, window.location.origin);
    const token = pageUrl.searchParams.get("token");
    if (!token) return null;

    const apiBase = new URL(
      String(useFetch.defaults?.baseURL || ""),
      window.location.origin
    );
    const pdfUrl = new URL(`wallet/PublicReceipt/${transferId}`, apiBase.href);
    pdfUrl.searchParams.set("token", token);

    const expires = pageUrl.searchParams.get("expires");
    if (expires) {
      pdfUrl.searchParams.set("expires", expires);
    }
    return pdfUrl.toString();
  } catch (err) {
    console.error("build public receipt pdf url failed:", err);
    return null;
  }
}

async function sharePdfFileWithNativeSheet(
  transferId: string
): Promise<boolean> {
  const pdf = await fetchReceiptPdfBlob(transferId);
  if (!pdf || typeof navigator.share !== "function") return false;

  const file = new File([pdf.blob], pdf.fileName, {
    type: "application/pdf",
  });
  if (
    typeof navigator.canShare === "function" &&
    !navigator.canShare({ files: [file] })
  ) {
    return false;
  }

  try {
    await navigator.share({ files: [file] });
    return true;
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      throw err;
    }
    console.error("native PDF file share failed:", err);
    return false;
  }
}

// Rasterize a DOM node to PNG via html-to-image, then embed that bitmap
// in a single-page A4 PDF (portrait) with the original aspect ratio.
// Both the P2P post-send receipt and the history detail sheet share this
// pipeline so a chek saved from either path comes out identical.
export async function renderNodeToPdf({
  node,
  completedAt,
  backgroundColor,
}: RenderOptions): Promise<{ blob: Blob; fileName: string }> {
  const dataUrl = await toPng(node, {
    pixelRatio: 2,
    backgroundColor,
    cacheBust: true,
  });

  const img = new Image();
  img.src = dataUrl;
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Failed to decode receipt image"));
  });

  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const maxWidth = pageWidth - margin * 2;
  const maxHeight = pageHeight - margin * 2;
  const aspect = img.naturalWidth / img.naturalHeight;
  let drawW = maxWidth;
  let drawH = drawW / aspect;
  if (drawH > maxHeight) {
    drawH = maxHeight;
    drawW = drawH * aspect;
  }
  const x = (pageWidth - drawW) / 2;
  const y = margin;
  pdf.addImage(dataUrl, "PNG", x, y, drawW, drawH);

  const blob = pdf.output("blob");
  const stamp = completedAt
    .toISOString()
    .slice(0, 19)
    .replace(/[:T]/g, "-");
  return { blob, fileName: `don-chek-${stamp}.pdf` };
}

// Trigger a PDF download via a synthetic anchor. Important details:
//   1. The anchor is attached to the DOM before .click() and removed
//      after — some Chromium builds silently no-op a detached click.
//   2. `target="_blank"` is always set so the PDF can open in a new
//      tab where the browser supports it.
//   3. The `download` attribute is intentionally OMITTED on Android /
//      iOS Telegram WebView. There, `download` writes the file to an
//      invisible internal directory ("saqlandi but where?" — the
//      Android Telegram bug users hit). Skipping the attribute makes
//      the PDF open in the WebView's tab / a PDF viewer, where the
//      user can see it and use the viewer's own share/save controls.
//      Desktop keeps the `download` attribute so files still land in
//      the OS Downloads folder.
function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const tg = (window as unknown as {
    Telegram?: { WebApp?: { platform?: string } };
  }).Telegram?.WebApp;
  const platform = tg?.platform;
  const isMobileTelegram = platform === "android" || platform === "ios";

  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener";
  if (!isMobileTelegram) {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Keep the URL alive long enough for the new tab / viewer to finish
  // loading the PDF; the desktop download path only needs ~1s but we
  // don't know which branch the browser actually took.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

// Try the OS share sheet on every platform that exposes file-share
// support — payment platforms (Click / Payme / Apelsin) show share on
// desktop too, so we match that. Falls back to a direct download (or
// open-in-tab via target=_blank) when share isn't available or fails.
// Returns "shared" | "downloaded" so callers can pick the right toast.
export async function saveOrSharePdf({
  blob,
  fileName,
  transferId,
}: SaveOrShareOptions): Promise<"shared" | "downloaded" | "bot-sent"> {
  const file = new File([blob], fileName, { type: "application/pdf" });

  // Mobile Telegram first: skip navigator.share (unreliable on Android
  // WebView, and download fallback writes to an invisible folder) and
  // route the PDF through the bot straight into the user's chat — same
  // UX as Click / Payme. Only used when the caller passed a transferId
  // for ownership validation backend-side.
  if (isMobileTelegram() && transferId) {
    const ok = await sendReceiptToChat(blob, fileName, transferId);
    if (ok) return "bot-sent";
    // Bot send failed (network, auth, telegram_id missing) — fall
    // through so the user still gets *something* via the standard
    // download path.
  }

  // Try navigator.share whenever the API exists. We deliberately do NOT
  // gate on navigator.canShare({files}) — on some WebViews canShare
  // returns false for PDFs even though share itself will happily open
  // the system share sheet, and gating made the share button silently
  // fall through to download (the "saved but where?" bug). If share
  // actually throws, the catch falls through to the download path.
  // We intentionally OMIT title/text so the recipient app (Telegram
  // especially) gets *only* the PDF. Passing text made Telegram send
  // the amount string ("12 345 DON") as a separate chat message
  // alongside the file — user reported they only want the chek.
  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ files: [file] });
      return "shared";
    } catch (err) {
      // User dismissed the share sheet — leave silently, no fallback
      // download because they actively chose not to save.
      if (err instanceof Error && err.name === "AbortError") {
        return "shared";
      }
      // Any other share failure → fall through to direct download so
      // the user still gets the file.
    }
  }

  triggerDownload(blob, fileName);
  return "downloaded";
}

interface ShareReceiptOptions {
  /** WalletTransfer.id — required to use the backend-render fast path. */
  transferId: string | null;
  /** Lazy: only invoked when we actually need a client-rendered blob. */
  render: () => Promise<{ blob: Blob; fileName: string }>;
}

// High-level entry point for the P2P receipt share button. Goal: a
// real share sheet on every platform — no bot-chat drop fallback.
//
// Telegram path (sender is inside a Telegram WebApp):
//   1. shareMessage (Mini Apps v8.0+) → native Telegram contact picker
//      with the PDF attached as a chat document. Best UX, works on
//      iOS / macOS where v8 is supported.
//   2. If shareMessage isn't available OR fails (Android pre-v8, or
//      Android v8 with the silent-no-op bug) → shareURL with a
//      token-signed public PDF URL. Telegram still opens its native
//      contact picker on every client; recipient gets a chat message
//      with the URL, clicks it, downloads the PDF.
//
// Non-Telegram browser path: fetch PDF blob, navigator.share, fall
// back to triggerDownload.
//
// No-transferId path: legacy client-render.
export async function shareReceipt({
  transferId,
  render,
}: ShareReceiptOptions): Promise<"shared" | "downloaded" | "bot-sent"> {
  if (!transferId) {
    const { blob, fileName } = await render();
    return saveOrSharePdf({ blob, fileName, transferId });
  }

  // Android Telegram: every direct Telegram share API is broken here
  // (shareMessage / shareURL silently no-op). Fight harder for the
  // system share sheet before falling back:
  //   1. Fetch BOTH share URL (HMAC-signed PDF link) and PDF blob in
  //      parallel so neither variant has to wait for the other later.
  //   2. Try navigator.share({files: [pdf]}) — same shape iOS uses,
  //      gated by navigator.canShare so we don't trip the WebView's
  //      "files not supported" silent rejection.
  //   3. Try navigator.share({url, text, title}) — older Web Share
  //      shape (Chrome 75+), more compatible than {files}.
  //   4. Only if BOTH share variants are unsupported / refused, fall
  //      back to Telegram's downloadFile so the user at least leaves
  //      with the PDF.
  // Errors from each step are logged so the in-app diagnostic toast
  // (TransferReceipt) surfaces the specific reason if everything fails.
  if (isAndroidTelegram()) {
    const [urlRes, pdfRes] = await Promise.all([
      useFetch.post<{ share_url: string }>(
        "wallet/ReceiptShareUrl",
        { transfer_id: transferId },
        { timeout: 15_000 }
      ),
      useFetch.post<Blob>(
        "wallet/RenderReceiptPdf",
        { transfer_id: transferId },
        { timeout: 15_000, responseType: "blob" }
      ),
    ]);
    const publicUrl = urlRes.data.share_url;
    const downloadUrl =
      buildPublicReceiptPdfUrl(publicUrl, transferId) || publicUrl;
    const fileName = `don-chek-${transferId}.pdf`;
    const pdfFile = new File([pdfRes.data], fileName, {
      type: "application/pdf",
    });

    const hasShare = typeof navigator.share === "function";
    const canShareFiles =
      hasShare &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [pdfFile] });
    const canShareUrl =
      hasShare &&
      (typeof navigator.canShare !== "function" ||
        navigator.canShare({ url: publicUrl }));

    // Variant 1: share the PDF file itself — same UX iOS users see.
    if (canShareFiles) {
      try {
        await navigator.share({ files: [pdfFile] });
        return "shared";
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return "shared";
        }
        console.error("navigator.share(files) failed:", err);
      }
    }

    // Variant 2: share just the URL — broader compatibility.
    if (canShareUrl) {
      try {
        await navigator.share({ url: publicUrl });
        return "shared";
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return "shared";
        }
        console.error("navigator.share(url) failed:", err);
      }
    }

    // Last resort: native Telegram download popup. Mini-app stays
    // open, user taps "Download" and the PDF lands in their Downloads.
    if (!downloadFile.isAvailable()) {
      throw new Error("share_and_download_unsupported");
    }
    await downloadFile(downloadUrl, fileName);
    return "downloaded";
  }

  if (isAnyTelegramWebApp()) {
    const prepared = await fetchPreparedShare(transferId);
    if (prepared) {
      // Primary: shareMessage with the prepared inline message.
      if (prepared.preparedMessageId && shareMessage.isAvailable()) {
        try {
          await shareMessage(prepared.preparedMessageId);
          return "shared";
        } catch (err) {
          // Fall through to shareURL so older clients still get a sheet.
          console.error("shareMessage failed:", err);
        }
      }
      // Fallback: shareURL with the public download link. Opens
      // t.me/share/url which works on iOS / macOS / desktop Telegram
      // (Android handled in the dedicated branch above).
      if (prepared.shareUrl && shareURL.isAvailable()) {
        try {
          shareURL(prepared.shareUrl);
          return "shared";
        } catch (err) {
          console.error("shareURL failed:", err);
        }
      }
    }
  }

  // Non-Telegram browser: fetch PDF, native share, download fallback.
  const pdf = await fetchReceiptPdfBlob(transferId);
  if (pdf) {
    const file = new File([pdf.blob], pdf.fileName, {
      type: "application/pdf",
    });
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ files: [file] });
        return "shared";
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return "shared";
        }
      }
    }
    triggerDownload(pdf.blob, pdf.fileName);
    return "downloaded";
  }

  // PDF fetch failed entirely — last-ditch client-render path so the
  // user still leaves with something.
  const { blob, fileName } = await render();
  return saveOrSharePdf({ blob, fileName, transferId });
}

/* ---- Bottom-sheet actions ----
   The Android share-API rabbit hole eventually showed every direct
   share path is unreliable (shareMessage / shareURL silent no-op,
   navigator.share blocked or refused). The chek button on the
   receipt page opens an in-app Drawer so the user explicitly picks
   the channel. The two helpers below back the two buttons inside that
   Drawer. Each throws with a descriptive Error so the sheet can
   surface the failure reason in a toast. */

/** Share the chek through Telegram's native contact picker.
 *
 *  Preferred path: a real PDF *document* (shareMessage) — no link shown.
 *  This needs a valid Mini App session for savePreparedInlineMessage and a
 *  client that supports shareMessage. If that fails, we try native file
 *  sharing. We deliberately do not fall back to URL sharing here because the
 *  desired UX is a PDF file only, consistently on iOS and Android. */
export async function shareReceiptToContact(
  transferId: string
): Promise<"shared"> {
  const prepared = await fetchPreparedShare(transferId);
  if (prepared?.preparedMessageId) {
    const { shareMessage } = await import("@telegram-apps/sdk-react");
    if (shareMessage.isAvailable()) {
      try {
        await shareMessage(prepared.preparedMessageId);
        return "shared";
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          throw err;
        }
        console.error("shareMessage failed, trying native PDF share:", err);
      }
    }
  }

  // Fallback 1: native file share. Android Telegram's shareMessage can no-op,
  // but the Web Share file path can still open a system sheet that sends the
  // actual PDF instead of a visible link.
  if (await sharePdfFileWithNativeSheet(transferId)) {
    return "shared";
  }

  throw new Error("receipt_pdf_share_unavailable");
}

/** Telegram-native download popup. User taps "Download" and the PDF
 *  lands in their device's Downloads folder. Mini-app stays open. */
export async function downloadReceiptToDevice(
  transferId: string
): Promise<void> {
  const res = await useFetch.post<{ share_url: string }>(
    "wallet/ReceiptShareUrl",
    { transfer_id: transferId },
    { timeout: 15_000 }
  );
  const pdfUrl = buildPublicReceiptPdfUrl(res.data.share_url, transferId);
  if (pdfUrl) {
    const { downloadFile } = await import("@telegram-apps/sdk-react");
    if (downloadFile.isAvailable()) {
      await downloadFile(pdfUrl, `don-chek-${transferId}.pdf`);
      return;
    }
  }

  const pdf = await fetchReceiptPdfBlob(transferId);
  if (!pdf) {
    throw new Error("receipt_pdf_unavailable");
  }
  triggerDownload(pdf.blob, pdf.fileName);
}
